import { Button } from 'primereact/button';
import { FC, MutableRefObject, Suspense, useEffect, useRef, useState } from 'react';
import { SkillAssessmentTableFilters, SkillAssessmentTableItem } from './SkillAssessmentTable';
interface Props {
  filters: SkillAssessmentTableFilters;
  totalRecords: number;
  hideButton?: number | null;
}
const PAGE_SIZE = 10000;
export const DownloadCsvAssessmentHistoryBtn: FC<Props> = ({ filters, totalRecords, hideButton = null }) => {
  const [fetchData, setFetchData] = useState(false);
  const [finishedLoading, setFinishedLoading] = useState(false);
  const [localFilters, setLocalFilters] = useState({ ...filters, page: 0 });
  const [skillAssessmentFiltered, setSkillAssessmentsFiltered] = useState<SkillAssessmentTableItem[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const dispatch = useAppDispatch();
  const isCsvProcessing = useAppSelector(selectedCsvProcess);
  useEffect(() => {
    setLocalFilters({ ...filters, page: 0, rows: PAGE_SIZE });
  }, [filters]);

  const totalOfQuerys = Math.ceil(totalRecords / PAGE_SIZE);
  const [queryNumber, setQueryNumber] = useState<number>(1);
  const [progressValueToast, setProgressValueToast] = useState<number>(0);
  const interval = useRef<number | null>(null);
  const downloadToastRef = useRef<Toast | null>(null);
  const successfullToastRef = useRef<Toast | null>(null);

  const clear = async () => {
    setTimeout(() => {
      setSkillAssessmentsFiltered([]);
      setFetchData(false);
      setFinishedLoading(false);
      setPageInfo(undefined);
      clearDownloadToast(setQueryNumber, setProgressValueToast, interval, downloadToastRef);
    }, 500);
  };

  useEffect(() => {
    setLocalFilters({ ...filters, page: 0, rows: PAGE_SIZE });
  }, [filters]);

  useEffect(() => {
    if (finishedLoading) {
      exportToCsvAssessmentHistoryTable(skillAssessmentFiltered, AssessmentHistoryType.SKILL_ASSESSMENTS)
        .then(() => {
          clear().then(() => {
            showSuccessfullToast(successfullToastRef);
            setTimeout(() => {
              dispatch(setCsvProcess(false));
              dispatch(setReportHubQuerysInProgress(false));
            }, 3000);
          });
        })
        .catch(() => {
          dispatch(setCsvProcess(false));
          dispatch(setReportHubQuerysInProgress(false));
        });
    }
  }, [finishedLoading]);

  useEffect(() => {
    if (fetchData && pageInfo && !pageInfo?.hasNextPage) {
      setFinishedLoading(true);
    }
  }, [pageInfo]);

  useEffect(() => {
    // emulated onClick for ReportHub feature
    if (hideButton) {
      dispatch(setCsvProcess(true));
      setFetchData(true);
      showDownloadToast(downloadToastRef);
    }
  }, [hideButton]);

  return (
    <>
      {!hideButton && (
        <Button
          icon="nc_icon-nc_export icon-sm"
          label="Export Data Table"
          tooltip="Export the filtered data to CSV format"
          aria-label="Export the filtered data to CSV format"
          rounded
          outlined
          disabled={fetchData || isCsvProcessing}
          onClick={() => {
            dispatch(setCsvProcess(true));
            setFetchData(true);
            showDownloadToast(downloadToastRef);
          }}
        ></Button>
      )}
      {fetchData && !finishedLoading && (
        <Suspense>
          <AssessmentHistoryQueryDataCsv
            skillAssessments={skillAssessmentFiltered}
            fetchData={fetchData}
            filterState={localFilters}
            setSkillAssessments={setSkillAssessmentsFiltered}
            pageInfo={pageInfo}
            setPageInfo={setPageInfo}
            setProgressValueToast={setProgressValueToast}
            totalOfQuerys={totalOfQuerys}
            interval={interval}
            queryNumber={queryNumber}
            setQueryNumber={setQueryNumber}
          />
        </Suspense>
      )}
      <ToastExportXLSorCSV downloadToastRef={downloadToastRef} clear={clear} progressValueToast={progressValueToast} />
      <ToastSuccessful successfullToastRef={successfullToastRef} />
    </>
  );
};

import { Dispatch, SetStateAction } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { getSkillAssessments } from '../../graphQLOperations/Monitoring';
import { MonitoringSkillAssessmentsQuery } from '../../graphQLOperations/__generated__/MonitoringSkillAssessmentsQuery.graphql';
import { PageInfo } from '../../models/PageInfo';
import { sortOrderCodeForQueries } from '../../businessLogicService/sortOrderCodeForQueries';
import { getDateFiltersForQueries } from '../../utils/getDateFiltersForQueries';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectedCsvProcess, setCsvProcess } from '../../redux/reducers/csvProcessSlice';
import { Toast } from 'primereact/toast';
import {
  clearDownloadToast,
  showDownloadToast,
  showSuccessfullToast,
} from '../../businessLogicService/exportToastHandler';
import ToastExportXLSorCSV from '../ExportCSV/ToastExportXLSorCSV';
import ToastSuccessful from '../ExportCSV/ToastSuccessful';
import { exportToCsvAssessmentHistoryTable } from '../../businessLogicService/exportCSVAssessmentHistoryTable';
import { AssessmentHistoryType } from '../../models/CsvExport';
import { setReportHubQuerysInProgress } from '../../redux/reducers/reportHubQuerysInProgress';

export interface QuerySkillAssessmentsDataProp {
  skillAssessments: SkillAssessmentTableItem[];
  fetchData: boolean;
  filterState: SkillAssessmentTableFilters;
  setSkillAssessments: Dispatch<SetStateAction<SkillAssessmentTableItem[]>>;
  setPageInfo: Dispatch<SetStateAction<PageInfo | undefined>>;
  pageInfo: PageInfo | undefined;
  setProgressValueToast: (p: number) => void;
  totalOfQuerys: number;
  interval: MutableRefObject<number | null | undefined>;
  queryNumber: number;
  setQueryNumber: Dispatch<SetStateAction<number>>;
}

export const AssessmentHistoryQueryDataCsv = ({
  setSkillAssessments,
  filterState,
  fetchData,
  setPageInfo,
  skillAssessments,
  pageInfo,
  setProgressValueToast,
  totalOfQuerys,
  interval,
  queryNumber,
  setQueryNumber,
}: QuerySkillAssessmentsDataProp) => {
  const isSkillCode = filterState.filters.skillNameCode?.split('').every(char => !isNaN(Number(char)));
  const { startDateAndTime, endDateAndTime } = getDateFiltersForQueries(filterState.filters.completedOn);

  const skillAsssessmentData = useLazyLoadQuery<MonitoringSkillAssessmentsQuery>(
    getSkillAssessments,
    {
      filter: {
        classIds: filterState.filters.classes,
        targetGroupIds: filterState.filters.roadmaps,
        standardGradeIds: filterState.filters.skillGrades,
        studentName: filterState.filters.studentName,
        gradeLevelIds: filterState.filters.gradeLevels,
        skillName: !isSkillCode ? filterState.filters.skillNameCode : null,
        skillCode: isSkillCode ? filterState.filters.skillNameCode : null,
        assessmentScores: filterState.filters.score,
        assessmentCompletedFrom: startDateAndTime ? startDateAndTime : null,
        assessmentCompletedTo: endDateAndTime ? endDateAndTime : null,
      },
      first: filterState.rows,
      last: null,
      order: [
        {
          user: {
            lastName: sortOrderCodeForQueries(filterState.sortOrder),
            firstName: sortOrderCodeForQueries(filterState.sortOrder),
          },
          skill: { id: 'ASC' },
          sessionNumber: sortOrderCodeForQueries(filterState.sortOrderAttempted),
          sessionCompletedOn: 'DESC',
        },
      ],
      after: pageInfo?.endCursor,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(filterState) },
  );

  useEffect(() => {
    if (queryNumber <= totalOfQuerys) {
      const calc = Math.round((queryNumber / totalOfQuerys) * 100);
      interval.current = calc;
      setProgressValueToast(calc);
      setQueryNumber((e: number) => e + 1);
    }
    if (skillAsssessmentData.studentsSkillAssessments?.nodes && fetchData) {
      setPageInfo(skillAsssessmentData.studentsSkillAssessments.pageInfo);
      {
        setSkillAssessments([
          ...skillAssessments,
          ...(skillAsssessmentData.studentsSkillAssessments.nodes?.map(skillAsssessment => ({
            id: skillAsssessment.id || '',
            userId: skillAsssessment.user?.id || '',
            skill: skillAsssessment.skill || null,
            studentName: skillAsssessment.user?.lastName + ', ' + skillAsssessment.user?.firstName,
            publicUserId: skillAsssessment.user?.publicUserId ?? '',
            skillName: skillAsssessment.skill?.name || '',
            score: skillAsssessment.score || '',
            completedOn: skillAsssessment.sessionCompletedOn || '',
            attempted: skillAsssessment.sessionNumber,
          })) ?? []),
        ]);
      }
    }
  }, [skillAsssessmentData]);

  return <div style={{ display: 'none' }}></div>;
};
